/**
 * ComplianceDashboardPage.tsx — Real-Time Regulatory Compliance Dashboard
 *
 * Multi-jurisdiction regulatory validation engine. Every sample parameter
 * is auto-checked against the selected jurisdiction's discharge limits in
 * real time. Supports:
 *   • UK Environment Agency (UK EA)
 *   • Australian EPA (AU EPA)
 *   • SA Water — Trade Waste (SA Water)
 *   • EU Urban Wastewater Treatment Directive (EU UWWTD)
 *   • US EPA — NPDES (US EPA)
 *
 * Default standard is pulled from the active session. All 5 jurisdictions
 * can be displayed side-by-side in the Comparison Matrix for gap analysis.
 */

import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell, ReferenceLine, LineChart, Line,
} from 'recharts';
import {
  Shield, ShieldCheck, ShieldAlert, AlertTriangle, CheckCircle2,
  XCircle, RefreshCw, ChevronRight, Info, Zap, Activity,
  BarChart3, Clock, Globe, ArrowRight, FlaskConical, Beaker,
  TrendingUp, TrendingDown, Minus, Save, FileText, Printer,
  ClipboardList, Circle, Building2, Layers, Database, Droplets,
  SlidersHorizontal, Eye,
} from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { useSession } from '@/context/SessionContext';
import { toast } from 'sonner';
import { getAllLocalSamples } from '@/lib/hybridStorage';
import { apiListInfluentSamples, apiSaveReport, hasPhpBackend } from '@/lib/phpApi';
import { ALL_STANDARDS, type StandardCode } from '@/types/standards';

// ─── Types ─────────────────────────────────────────────────────────────────

interface SampleRow {
  id: number;
  sampleName: string;
  params: Record<string, number>;
  createdAt: string;
  sampleType?: string | null;
}

interface ParamResult {
  key: string;
  label: string;
  unit: string;
  value: number;
  limit: number;
  ratio: number;           // value / limit
  pass: boolean;
  margin: number;          // % under (+) or over (-) limit
  status: 'compliant' | 'near_limit' | 'exceeded' | 'critical';
  color: string;
}

interface JurisdictionScore {
  code: StandardCode;
  name: string;
  shortLabel: string;
  flag: string;
  accentColor: string;
  complianceScore: number;
  passCount: number;
  totalChecked: number;
  failures: string[];
}

// ─── Constants ─────────────────────────────────────────────────────────────

const JURISDICTIONS: { code: StandardCode; name: string; shortLabel: string; flag: string; accentColor: string; description: string }[] = [
  { code: 'UK_EA',    name: 'UK Environment Agency',            shortLabel: 'UK EA',    flag: '🇬🇧', accentColor: '#3b82f6', description: 'UK statutory discharge consent limits — Environment Act 1995. Conservative limits protecting sensitive water bodies.' },
  { code: 'AU_EPA',   name: 'Australian EPA',                   shortLabel: 'AU EPA',   flag: '🇦🇺', accentColor: '#10b981', description: 'Australian state EPA trade waste guidelines. Limits vary by receiving water sensitivity.' },
  { code: 'SA_WATER', name: 'SA Water — Trade Waste',           shortLabel: 'SA Water', flag: '💧', accentColor: '#06b6d4', description: 'SA Water trade waste acceptance criteria for discharge to sewer. Applies to SA Water sewerage network.' },
  { code: 'EU_UWWTD', name: 'EU Urban Wastewater Directive',    shortLabel: 'EU UWWTD', flag: '🇪🇺', accentColor: '#a78bfa', description: 'European Council Directive 91/271/EEC. Applies to municipal and industrial discharges into EU watercourses.' },
  { code: 'US_EPA',   name: 'US EPA — NPDES',                   shortLabel: 'US EPA',   flag: '🇺🇸', accentColor: '#f97316', description: 'US National Pollutant Discharge Elimination System. Stringent heavy metals and nutrient limits for surface water discharge.' },
];

const PARAM_DEFS: { key: string; label: string; unit: string; category: 'organic' | 'physical' | 'nutrients' | 'metals' | 'other' }[] = [
  { key: 'bod5',            label: 'BOD₅',          unit: 'mg/L', category: 'organic'   },
  { key: 'cod',             label: 'COD',            unit: 'mg/L', category: 'organic'   },
  { key: 'tss',             label: 'TSS',            unit: 'mg/L', category: 'physical'  },
  { key: 'oilGrease',       label: 'Oil & Grease',   unit: 'mg/L', category: 'organic'   },
  { key: 'ph',              label: 'pH',             unit: '',     category: 'physical'  },
  { key: 'tds',             label: 'TDS',            unit: 'mg/L', category: 'physical'  },
  { key: 'temperature',     label: 'Temperature',    unit: '°C',   category: 'physical'  },
  { key: 'conductivity',    label: 'Conductivity',   unit: 'µS/cm',category: 'physical'  },
  { key: 'ammoniaN',        label: 'NH₃-N',          unit: 'mg/L', category: 'nutrients' },
  { key: 'totalNitrogen',   label: 'Total N',        unit: 'mg/L', category: 'nutrients' },
  { key: 'totalPhosphorus', label: 'Total P',        unit: 'mg/L', category: 'nutrients' },
  { key: 'lead',            label: 'Lead (Pb)',      unit: 'mg/L', category: 'metals'    },
  { key: 'mercury',         label: 'Mercury (Hg)',   unit: 'mg/L', category: 'metals'    },
  { key: 'cadmium',         label: 'Cadmium (Cd)',   unit: 'mg/L', category: 'metals'    },
  { key: 'chromium',        label: 'Chromium (Cr)',  unit: 'mg/L', category: 'metals'    },
  { key: 'arsenic',         label: 'Arsenic (As)',   unit: 'mg/L', category: 'metals'    },
  { key: 'nickel',          label: 'Nickel (Ni)',    unit: 'mg/L', category: 'metals'    },
  { key: 'zinc',            label: 'Zinc (Zn)',      unit: 'mg/L', category: 'metals'    },
  { key: 'copper',          label: 'Copper (Cu)',    unit: 'mg/L', category: 'metals'    },
  { key: 'sulfides',        label: 'Sulfides',       unit: 'mg/L', category: 'other'     },
  { key: 'phenol',          label: 'Phenol',         unit: 'mg/L', category: 'other'     },
  { key: 'surfactants',     label: 'Surfactants',    unit: 'mg/L', category: 'other'     },
  { key: 'chlorides',       label: 'Chlorides',      unit: 'mg/L', category: 'other'     },
];

const CATEGORY_COLORS: Record<string, string> = {
  organic: '#f97316', physical: '#38bdf8', nutrients: '#a78bfa', metals: '#ef4444', other: '#94a3b8',
};

// ─── Helpers ───────────────────────────────────────────────────────────────

function getLimitsForStandard(code: StandardCode): Record<string, number> {
  const std = ALL_STANDARDS.find(s => s.code === code);
  if (!std) return {};
  const L = std.limits;
  return {
    bod5: L.bod5, cod: L.cod, tss: L.tss,
    oilGrease: L.oilGrease, ph: L.phMax, tds: L.tds,
    temperature: L.temperature, conductivity: L.conductivity,
    ammoniaN: L.ammoniaN, totalNitrogen: L.totalNitrogen,
    totalPhosphorus: L.totalPhosphorus,
    lead: L.lead, mercury: L.mercury, cadmium: L.cadmium,
    chromium: L.chromium, arsenic: L.arsenic,
    nickel: L.nickel, zinc: L.zinc, copper: L.copper,
    sulfides: L.sulfides, phenol: L.phenol,
    surfactants: L.surfactants, chlorides: L.chlorides,
  };
}

function mapParams(raw: Record<string, number>): Record<string, number> {
  return {
    bod5: raw.bod5 ?? raw.bod ?? 0,
    cod: raw.cod ?? 0, tss: raw.tss ?? 0,
    oilGrease: raw.oilGrease ?? raw.og ?? raw.oandg ?? 0,
    ph: raw.ph ?? 7, tds: raw.tds ?? 0,
    temperature: raw.temperature ?? 20,
    conductivity: raw.conductivity ?? 0,
    ammoniaN: raw.ammoniaN ?? raw.nh3n ?? raw.nh3 ?? 0,
    totalNitrogen: raw.totalNitrogen ?? raw.tn ?? 0,
    totalPhosphorus: raw.totalPhosphorus ?? raw.tp ?? 0,
    lead: raw.lead ?? 0, mercury: raw.mercury ?? 0,
    cadmium: raw.cadmium ?? 0, chromium: raw.chromium ?? 0,
    arsenic: raw.arsenic ?? 0, nickel: raw.nickel ?? 0,
    zinc: raw.zinc ?? 0, copper: raw.copper ?? 0,
    sulfides: raw.sulfides ?? raw.sulfide ?? 0,
    phenol: raw.phenol ?? 0, surfactants: raw.surfactants ?? 0,
    chlorides: raw.chlorides ?? raw.chloride ?? 0,
  };
}

function calcCompliance(params: Record<string, number>, limits: Record<string, number>): ParamResult[] {
  return PARAM_DEFS.map(def => {
    const value = params[def.key] ?? 0;
    const limit = limits[def.key] ?? 0;
    if (value <= 0 || limit <= 0) return null;

    const ratio = value / limit;
    const margin = ((limit - value) / limit) * 100;
    const pass = value <= limit;

    const status: ParamResult['status'] =
      !pass && ratio > 2  ? 'critical'   :
      !pass               ? 'exceeded'   :
      ratio > 0.8         ? 'near_limit' : 'compliant';

    const color =
      status === 'critical'   ? '#ef4444' :
      status === 'exceeded'   ? '#f97316' :
      status === 'near_limit' ? '#f59e0b' : '#10b981';

    return { key: def.key, label: def.label, unit: def.unit, value, limit, ratio, pass, margin, status, color } satisfies ParamResult;
  }).filter((x): x is ParamResult => x !== null);
}

function calcJurisdictionScore(params: Record<string, number>, code: StandardCode): JurisdictionScore {
  const j = JURISDICTIONS.find(j => j.code === code)!;
  const limits = getLimitsForStandard(code);
  const results = calcCompliance(params, limits);
  const pass = results.filter(r => r.pass).length;
  const score = results.length > 0 ? Math.round((pass / results.length) * 100) : 100;
  return {
    code, name: j.name, shortLabel: j.shortLabel, flag: j.flag, accentColor: j.accentColor,
    complianceScore: score, passCount: pass, totalChecked: results.length,
    failures: results.filter(r => !r.pass).map(r => r.label),
  };
}

function fmtVal(v: number): string {
  if (v === 0) return '—';
  return v < 0.01 ? v.toExponential(2) : v < 10 ? v.toFixed(3) : v.toFixed(v < 100 ? 1 : 0);
}

// ─── Sub-components ─────────────────────────────────────────────────────────

function ComplianceRing({ score, color, size = 96 }: { score: number; color: string; size?: number }) {
  const r = size * 0.42;
  const circ = 2 * Math.PI * r;
  const dash = (score / 100) * circ;
  const cx = size / 2, cy = size / 2;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="rgba(100,116,139,0.12)" strokeWidth={size * 0.085} />
      <circle cx={cx} cy={cy} r={r} fill="none" stroke={color} strokeWidth={size * 0.085}
        strokeDasharray={`${dash} ${circ}`} strokeLinecap="round"
        style={{ transformOrigin: `${cx}px ${cy}px`, transform: 'rotate(-90deg)', transition: 'stroke-dasharray 0.7s ease' }} />
      <text x={cx} y={cy - 3} textAnchor="middle" fill="white" fontSize={size * 0.2} fontWeight={700}>{score}%</text>
      <text x={cx} y={cy + size * 0.13} textAnchor="middle" fill="#94a3b8" fontSize={size * 0.09}>compliant</text>
    </svg>
  );
}

function StatusPill({ status }: { status: ParamResult['status'] }) {
  const map: Record<ParamResult['status'], string> = {
    compliant:  'bg-emerald-500/10 text-emerald-400 border-emerald-800/40',
    near_limit: 'bg-amber-500/10 text-amber-400 border-amber-800/40',
    exceeded:   'bg-red-500/10 text-red-400 border-red-800/40',
    critical:   'bg-red-600 text-white border-red-600',
  };
  const labels: Record<ParamResult['status'], string> = {
    compliant: 'PASS', near_limit: 'NEAR', exceeded: 'FAIL', critical: 'CRIT',
  };
  return <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded border ${map[status]}`}>{labels[status]}</span>;
}

function JurisdictionCard({ j, isActive, onClick }: { j: JurisdictionScore; isActive: boolean; onClick: () => void }) {
  const scoreColor =
    j.complianceScore >= 90 ? '#10b981' :
    j.complianceScore >= 70 ? '#84cc16' :
    j.complianceScore >= 50 ? '#f59e0b' :
    j.complianceScore >= 30 ? '#f97316' : '#ef4444';

  return (
    <button onClick={onClick}
      className={`relative text-left p-4 rounded-2xl border-2 transition-all overflow-hidden group ${isActive
        ? 'border-opacity-100'
        : 'border-slate-800/60 bg-rb-elevated hover:border-slate-600'}`}
      style={isActive ? { borderColor: j.accentColor, background: `${j.accentColor}0A` } : {}}>

      {/* Active glow */}
      {isActive && (
        <div className="absolute inset-0 pointer-events-none"
          style={{ background: `radial-gradient(ellipse at 30% 20%, ${j.accentColor}12 0%, transparent 70%)` }} />
      )}

      <div className="relative flex items-start justify-between gap-2 mb-3">
        <div>
          <div className="flex items-center gap-1.5 mb-0.5">
            <span className="text-lg">{j.flag}</span>
            <span className={`text-sm font-bold ${isActive ? 'text-slate-100' : 'text-slate-300'}`}>{j.shortLabel}</span>
            {isActive && (
              <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full text-slate-900" style={{ background: j.accentColor }}>ACTIVE</span>
            )}
          </div>
          <div className="text-[10px] text-slate-500 leading-tight">{j.failures.length === 0
            ? 'All parameters compliant'
            : `${j.failures.length} exceedance${j.failures.length > 1 ? 's' : ''}: ${j.failures.slice(0, 2).join(', ')}${j.failures.length > 2 ? '…' : ''}`}
          </div>
        </div>
        <ComplianceRing score={j.complianceScore} color={scoreColor} size={60} />
      </div>

      <div className="flex items-center justify-between text-[10px]">
        <span className="text-slate-500">{j.passCount}/{j.totalChecked} params pass</span>
        <div className="flex items-center gap-1" style={{ color: isActive ? j.accentColor : '#64748b' }}>
          <span className="font-bold">{isActive ? 'Selected' : 'Switch'}</span>
          <ChevronRight className="w-3 h-3" />
        </div>
      </div>

      {/* Bottom progress bar */}
      <div className="mt-2.5 h-1 bg-slate-800 rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all" style={{ width: `${j.complianceScore}%`, background: scoreColor }} />
      </div>
    </button>
  );
}

// ─── Main Page ──────────────────────────────────────────────────────────────

export default function ComplianceDashboardPage() {
  const { currentCustomer, activeStandard } = useSession();
  const [samples, setSamples] = useState<SampleRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [activeJurisdiction, setActiveJurisdiction] = useState<StandardCode>((activeStandard as StandardCode) ?? 'UK_EA');
  const [view, setView] = useState<'dashboard' | 'matrix' | 'detail' | 'report'>('dashboard');
  const [filterCategory, setFilterCategory] = useState<string | null>(null);
  const [lastValidated, setLastValidated] = useState<Date>(new Date());
  const [saving, setSaving] = useState(false);
  const validationRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Keep last-validated timestamp ticking
  useEffect(() => {
    validationRef.current = setInterval(() => setLastValidated(new Date()), 30000);
    return () => { if (validationRef.current) clearInterval(validationRef.current); };
  }, []);

  // Load effluent samples
  const loadSamples = useCallback(async () => {
    setLoading(true);
    try {
      const local = getAllLocalSamples()
        .filter(s => s.sampleType === 'effluent' || s.sampleType === 'influent' || !s.sampleType)
        .map((s): SampleRow => ({ id: s.id, sampleName: s.sampleName, params: s.params, createdAt: s.createdAt, sampleType: s.sampleType }));

      let server: SampleRow[] = [];
      if (hasPhpBackend()) {
        try {
          const res = await apiListInfluentSamples(currentCustomer?.serverId);
          server = (res ?? []).map((s: Record<string, unknown>): SampleRow => ({
            id: s.id as number,
            sampleName: (s.sampleName ?? s.sample_name ?? 'Sample') as string,
            params: (s.params ?? {}) as Record<string, number>,
            createdAt: (s.createdAt ?? s.created_at ?? '') as string,
            sampleType: (s.sampleType ?? s.sample_type ?? null) as string | null,
          }));
        } catch { /* offline */ }
      }

      const seen = new Set<number>();
      const merged = [...server, ...local].filter(s => !seen.has(s.id) && seen.add(s.id));
      merged.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      setSamples(merged);
      if (merged.length > 0 && !selectedId) setSelectedId(merged[0].id);
      setLastValidated(new Date());
    } finally {
      setLoading(false);
      setLoaded(true);
    }
  }, [currentCustomer, selectedId]);

  useEffect(() => { if (!loaded) loadSamples(); }, [loaded, loadSamples]);

  const selected = useMemo(() => samples.find(s => s.id === selectedId), [samples, selectedId]);
  const params = useMemo(() => selected ? mapParams(selected.params) : {}, [selected]);

  // Compute compliance for active jurisdiction
  const activeLimits = useMemo(() => getLimitsForStandard(activeJurisdiction), [activeJurisdiction]);
  const complianceResults = useMemo(() => calcCompliance(params, activeLimits), [params, activeLimits]);

  // Filtered results
  const filteredResults = useMemo(() =>
    filterCategory
      ? complianceResults.filter(r => PARAM_DEFS.find(d => d.key === r.key)?.category === filterCategory)
      : complianceResults,
    [complianceResults, filterCategory]);

  // Compliance score for active jurisdiction
  const complianceScore = useMemo(() => {
    if (complianceResults.length === 0) return 100;
    return Math.round(complianceResults.filter(r => r.pass).length / complianceResults.length * 100);
  }, [complianceResults]);

  // All 5 jurisdiction scores
  const allJurisdictionScores = useMemo((): JurisdictionScore[] => {
    if (Object.keys(params).length === 0) return JURISDICTIONS.map(j => ({
      ...j, complianceScore: 100, passCount: 0, totalChecked: 0, failures: [],
    }));
    return JURISDICTIONS.map(j => calcJurisdictionScore(params, j.code));
  }, [params]);

  const activeScore = allJurisdictionScores.find(j => j.code === activeJurisdiction);

  // Active jurisdiction info
  const activeJ = JURISDICTIONS.find(j => j.code === activeJurisdiction)!;

  // Radar chart — compliance ratio per param (capped at 150%)
  const radarData = useMemo(() =>
    complianceResults.slice(0, 8).map(r => ({
      subject: r.label, value: Math.min(150, Math.round(r.ratio * 100)), limit: 100,
    })),
    [complianceResults]
  );

  // Matrix comparison data (bar chart)
  const matrixData = useMemo(() =>
    allJurisdictionScores.map(j => ({
      name: j.shortLabel, score: j.complianceScore, color: j.accentColor,
      failures: j.failures.length,
    })),
    [allJurisdictionScores]
  );

  // Category breakdown
  const categories = useMemo(() => {
    const cats: Record<string, { total: number; passed: number }> = {};
    complianceResults.forEach(r => {
      const cat = PARAM_DEFS.find(d => d.key === r.key)?.category ?? 'other';
      if (!cats[cat]) cats[cat] = { total: 0, passed: 0 };
      cats[cat].total++;
      if (r.pass) cats[cat].passed++;
    });
    return Object.entries(cats).map(([cat, { total, passed }]) => ({
      category: cat, total, passed, score: Math.round((passed / total) * 100),
      color: CATEGORY_COLORS[cat] ?? '#94a3b8',
    }));
  }, [complianceResults]);

  const handleSaveReport = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const payload = {
        reportType: 'compliance_dashboard',
        customerId: currentCustomer?.serverId ?? null,
        sampleId: selected.id, sampleName: selected.sampleName,
        jurisdiction: activeJurisdiction,
        complianceScore, results: complianceResults,
        allJurisdictions: allJurisdictionScores,
        generatedAt: new Date().toISOString(),
        title: `Compliance Dashboard — ${activeJ.shortLabel} — ${selected.sampleName}`,
      };
      if (hasPhpBackend()) {
        await apiSaveReport(payload);
        toast.success('Compliance report saved to database');
      } else {
        localStorage.setItem(`rb_compliance_${selected.id}_${activeJurisdiction}`, JSON.stringify({ ...payload, savedAt: new Date().toISOString() }));
        toast.success('Compliance report saved locally');
      }
    } catch { toast.error('Failed to save report'); }
    finally { setSaving(false); }
  };

  const scoreColor =
    complianceScore >= 90 ? '#10b981' :
    complianceScore >= 70 ? '#84cc16' :
    complianceScore >= 50 ? '#f59e0b' :
    complianceScore >= 30 ? '#f97316' : '#ef4444';

  const riskLevel = complianceScore >= 90 ? 'Low' : complianceScore >= 70 ? 'Moderate' : complianceScore >= 50 ? 'High' : 'Critical';
  const riskColor = complianceScore >= 90 ? '#10b981' : complianceScore >= 70 ? '#f59e0b' : '#ef4444';

  const views = [
    { id: 'dashboard', label: 'Compliance Dashboard', short: 'Dashboard', icon: ShieldCheck },
    { id: 'matrix',    label: 'Jurisdiction Matrix',  short: 'Matrix',    icon: Globe },
    { id: 'detail',    label: 'Parameter Detail',     short: 'Detail',    icon: ClipboardList },
    { id: 'report',    label: 'Generate Report',      short: 'Report',    icon: FileText },
  ] as const;

  return (
    <div className="space-y-5 py-6 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">

      {/* Header */}
      <PageHeader
        icon={Shield}
        title="Compliance Dashboard"
        subtitle="Real-time regulatory validation — UK EA · AU EPA · SA Water · EU UWWTD · US EPA"
        iconColor="text-emerald-400"
        badge={
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            <span className="text-[11px] text-emerald-400 font-bold">Live Validation</span>
          </div>
        }
        actions={
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1.5 text-[11px] text-slate-500 border border-slate-700/60 rounded-lg px-2.5 py-1.5 bg-rb-elevated">
              <Clock className="w-3 h-3" />
              <span className="hidden sm:inline">Validated</span> {lastValidated.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
            </div>
            <button onClick={loadSamples} disabled={loading}
              className="px-3 py-1.5 text-xs font-bold text-slate-300 bg-rb-elevated border border-slate-700/60 hover:border-slate-500 hover:text-slate-100 rounded-lg transition-colors flex items-center gap-1.5">
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">Refresh</span>
            </button>
            <button onClick={handleSaveReport} disabled={saving || !selected}
              className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-colors flex items-center gap-1.5 ${
                selected
                  ? 'text-black bg-amber-400 hover:bg-amber-300 shadow-sm'
                  : 'text-slate-500 bg-rb-elevated border border-slate-700/60 cursor-not-allowed'
              }`}>
              <Save className="w-3.5 h-3.5" />
              {saving ? 'Saving…' : hasPhpBackend() ? 'Save to Database' : 'Save Report'}
            </button>
          </div>
        }
      />

      {/* Sample + Jurisdiction Selection Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Sample picker */}
        <div className="rb-card p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xs font-bold text-slate-400 flex items-center gap-2 uppercase tracking-wider">
              <Beaker className="w-3.5 h-3.5 text-emerald-400" /> Sample Source
            </h3>
            <span className="text-[10px] text-slate-600">{samples.length} available</span>
          </div>
          {loading && <div className="text-xs text-slate-600 text-center py-3 flex items-center justify-center gap-2"><RefreshCw className="w-3.5 h-3.5 animate-spin" />Loading…</div>}
          <div className="space-y-1.5 max-h-40 overflow-y-auto">
            {samples.length === 0 && !loading && (
              <p className="text-xs text-slate-600 text-center py-4">No samples found — add samples via Influent or Effluent Entry.</p>
            )}
            {samples.map(s => {
              const sel = s.id === selectedId;
              const date = new Date(s.createdAt.replace(' ', 'T')).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' });
              return (
                <button key={s.id} onClick={() => { setSelectedId(s.id); setLastValidated(new Date()); }}
                  className={`w-full text-left px-3 py-2 rounded-xl border-2 transition-all flex items-center justify-between ${sel
                    ? 'border-emerald-500 bg-emerald-500/8'
                    : 'border-slate-800/60 bg-rb-elevated hover:border-slate-700'}`}>
                  <div>
                    <div className="text-[11px] font-bold text-slate-200">{s.sampleName}</div>
                    <div className="text-[9px] text-slate-600">{date} · {s.sampleType ?? 'effluent'}</div>
                  </div>
                  {sel && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0" />}
                </button>
              );
            })}
          </div>
        </div>

        {/* Jurisdiction Cards */}
        <div className="lg:col-span-2 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
          {allJurisdictionScores.map(j => (
            <JurisdictionCard key={j.code} j={j} isActive={activeJurisdiction === j.code}
              onClick={() => { setActiveJurisdiction(j.code); setLastValidated(new Date()); }} />
          ))}
        </div>
      </div>

      {/* Only show analysis if sample selected */}
      {!selected ? (
        <div className="rb-card p-10 text-center">
          <Shield className="w-12 h-12 text-slate-700 mx-auto mb-3" />
          <div className="text-base font-bold text-slate-400 mb-1">Select a Sample to Validate</div>
          <p className="text-sm text-slate-500">Choose an effluent or influent sample above to run real-time compliance validation across all jurisdictions.</p>
        </div>
      ) : (
        <>
          {/* KPI Row */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { label: 'Compliance Score', value: `${complianceScore}%`, color: scoreColor, sub: `${complianceResults.filter(r => r.pass).length}/${complianceResults.length} parameters` },
              { label: 'Regulatory Risk', value: riskLevel, color: riskColor, sub: `${complianceResults.filter(r => !r.pass).length} exceedance${complianceResults.filter(r => !r.pass).length !== 1 ? 's' : ''}` },
              { label: 'Near Limit', value: String(complianceResults.filter(r => r.status === 'near_limit').length), color: '#f59e0b', sub: 'Within 20% of limit' },
              { label: 'Active Standard', value: activeJ.shortLabel, color: activeJ.accentColor, sub: activeJ.name.split(' — ')[0] },
            ].map((kpi, i) => (
              <div key={i} className="rb-card p-3.5 text-center">
                <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">{kpi.label}</div>
                <div className="text-xl font-bold mt-1" style={{ color: kpi.color }}>{kpi.value}</div>
                <div className="text-[10px] text-slate-600 mt-0.5 truncate">{kpi.sub}</div>
              </div>
            ))}
          </div>

          {/* View Tabs */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-1 mb-4">
            {views.map(v => {
              const Icon = v.icon;
              const active = view === v.id;
              return (
                <button key={v.id} onClick={() => setView(v.id)} title={v.label}
                  className={`flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-1.5 px-2 py-2 rounded-xl text-[10px] sm:text-xs font-bold transition-all border ${
                    active ? 'bg-amber-400 text-black border-amber-400/50 shadow-sm'
                           : 'bg-rb-surface text-slate-400 border-slate-700/50 hover:bg-rb-elevated hover:text-slate-100 hover:border-slate-600/70'
                  }`}>
                  <Icon className={`w-3.5 h-3.5 shrink-0 ${active ? 'text-black' : ''}`} />
                  <span className="leading-tight text-center sm:text-left">{v.short}</span>
                </button>
              );
            })}
          </div>

          <AnimatePresence mode="wait">
            <motion.div key={view} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }}>

              {/* ── DASHBOARD VIEW ──────────────────────────────────────────── */}
              {view === 'dashboard' && (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

                  {/* Left col: Ring + Category breakdown */}
                  <div className="space-y-4">
                    {/* Main compliance ring */}
                    <div className="rb-card p-5 flex flex-col items-center gap-4">
                      <ComplianceRing score={complianceScore} color={scoreColor} size={140} />
                      <div className="text-center">
                        <div className="text-base font-bold" style={{ color: activeJ.accentColor }}>{activeJ.flag} {activeJ.shortLabel}</div>
                        <div className="text-xs text-slate-500 mt-0.5">{activeJ.name}</div>
                        <div className="text-[10px] text-slate-600 mt-1 leading-relaxed max-w-[200px] mx-auto">{activeJ.description}</div>
                      </div>

                      {/* Live indicator */}
                      <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/8 border border-emerald-800/30">
                        <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                        <span className="text-[10px] text-emerald-400 font-bold">REAL-TIME VALIDATION ACTIVE</span>
                      </div>
                    </div>

                    {/* Category breakdown */}
                    <div className="rb-card p-4">
                      <div className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">Compliance by Category</div>
                      <div className="space-y-2">
                        {categories.map(cat => (
                          <button key={cat.category} onClick={() => setFilterCategory(filterCategory === cat.category ? null : cat.category)}
                            className={`w-full flex items-center gap-2.5 p-2 rounded-xl transition-all ${filterCategory === cat.category ? 'bg-rb-elevated border border-slate-700' : 'hover:bg-rb-elevated/60'}`}>
                            <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: cat.color }} />
                            <span className="text-xs text-slate-300 capitalize flex-1 text-left">{cat.category}</span>
                            <span className="text-[11px] font-mono font-bold" style={{ color: cat.score < 100 ? '#f97316' : '#10b981' }}>
                              {cat.passed}/{cat.total}
                            </span>
                            <div className="w-12 h-1.5 bg-slate-800 rounded-full overflow-hidden">
                              <div className="h-full rounded-full" style={{ width: `${cat.score}%`, background: cat.color }} />
                            </div>
                          </button>
                        ))}
                        {filterCategory && (
                          <button onClick={() => setFilterCategory(null)} className="text-[10px] text-slate-600 hover:text-slate-400 w-full text-center">Clear filter ✕</button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Middle + right: Radar + Exceedances */}
                  <div className="lg:col-span-2 space-y-4">
                    {/* Radar */}
                    <div className="rb-card p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <BarChart3 className="w-4 h-4" style={{ color: activeJ.accentColor }} />
                        <span className="text-sm font-bold text-slate-200">Parameter Compliance Radar — {activeJ.shortLabel}</span>
                      </div>
                      {radarData.length >= 3 ? (
                        <>
                          <ResponsiveContainer width="100%" height={260}>
                            <RadarChart data={radarData} margin={{ top: 10, right: 30, bottom: 10, left: 30 }}>
                              <PolarGrid />
                              <PolarAngleAxis dataKey="subject" tick={{ fontSize: 10, fill: '#94a3b8' }} />
                              <PolarRadiusAxis domain={[0, 150]} tick={false} axisLine={false} />
                              <Radar name="Value as % of limit" dataKey="value" stroke={activeJ.accentColor} fill={activeJ.accentColor} fillOpacity={0.15} strokeWidth={2} />
                              <Tooltip formatter={(v: number) => [`${v}% of limit`, '']} />
                            </RadarChart>
                          </ResponsiveContainer>
                          <p className="text-[10px] text-slate-600 text-center">Values shown as % of discharge limit. Red zone = exceedance (&gt;100%)</p>
                        </>
                      ) : (
                        <div className="h-[260px] flex flex-col items-center justify-center gap-2 text-slate-600">
                          <BarChart3 className="w-8 h-8 opacity-20" />
                          <p className="text-xs">Select a sample with ≥ 3 parameters to view radar</p>
                        </div>
                      )}
                    </div>

                    {/* Pass/Fail grid */}
                    <div className="rb-card p-4">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-sm font-bold text-slate-200">Parameter Validation Grid</span>
                        <span className="text-[10px] text-slate-500">{activeJ.flag} {activeJ.shortLabel} limits applied</span>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                        {(filterCategory
                          ? complianceResults.filter(r => PARAM_DEFS.find(d => d.key === r.key)?.category === filterCategory)
                          : complianceResults
                        ).map(r => (
                          <div key={r.key}
                            className={`p-2.5 rounded-xl border transition-all ${r.pass
                              ? 'bg-emerald-500/4 border-emerald-900/30'
                              : r.status === 'critical'
                              ? 'bg-red-500/10 border-red-700/40'
                              : 'bg-red-500/5 border-red-900/30'}`}>
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-[10px] font-bold text-slate-400">{r.label}</span>
                              <StatusPill status={r.status} />
                            </div>
                            <div className="flex items-end justify-between">
                              <div>
                                <div className="text-sm font-bold font-mono" style={{ color: r.color }}>{fmtVal(r.value)}</div>
                                <div className="text-[9px] text-slate-600">limit: {fmtVal(r.limit)} {r.unit}</div>
                              </div>
                              <div className="text-[10px] font-bold" style={{ color: r.color }}>
                                {Math.round(r.ratio * 100)}%
                              </div>
                            </div>
                            {/* Mini bar */}
                            <div className="mt-1.5 h-1 bg-slate-800 rounded-full overflow-hidden">
                              <div className="h-full rounded-full" style={{ width: `${Math.min(100, r.ratio * 100)}%`, background: r.color }} />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* ── JURISDICTION MATRIX ─────────────────────────────────────── */}
              {view === 'matrix' && (
                <div className="space-y-4">
                  {/* Score bar chart */}
                  <div className="rb-card p-5">
                    <div className="flex items-center gap-2 mb-4">
                      <Globe className="w-4 h-4 text-slate-400" />
                      <span className="text-sm font-bold text-slate-200">Multi-Jurisdiction Compliance Comparison</span>
                    </div>
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart data={matrixData} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(100,116,139,0.1)" />
                        <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#94a3b8' }} />
                        <YAxis domain={[0, 100]} tick={{ fontSize: 10, fill: '#64748b' }} />
                        <Tooltip formatter={(v: number) => [`${v}%`, 'Compliance score']} />
                        <ReferenceLine y={90} stroke="#10b981" strokeDasharray="4 2" label={{ value: 'Target 90%', fill: '#10b981', fontSize: 9 }} />
                        <Bar dataKey="score" radius={[6, 6, 0, 0]}>
                          {matrixData.map((m, i) => <Cell key={i} fill={m.color} />)}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Full comparison table */}
                  <div className="rb-card overflow-hidden">
                    <div className="px-5 py-3.5 border-b border-slate-800/50 bg-rb-elevated/50">
                      <span className="text-sm font-bold text-slate-200">Parameter-Level Compliance Matrix — All Jurisdictions</span>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="bg-rb-elevated text-[10px] uppercase tracking-wider text-slate-500">
                            <th className="text-left px-4 py-2.5 font-bold sticky left-0 bg-rb-elevated">Parameter</th>
                            <th className="text-right px-3 py-2.5 font-bold">Value</th>
                            {JURISDICTIONS.map(j => (
                              <th key={j.code} className="text-center px-3 py-2.5 font-bold min-w-[90px]" style={{ color: j.accentColor }}>
                                {j.flag} {j.shortLabel}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {PARAM_DEFS.map(def => {
                            const value = params[def.key] ?? 0;
                            if (value <= 0) return null;
                            return (
                              <tr key={def.key} className="border-t border-slate-800/40 hover:bg-rb-elevated/50">
                                <td className="px-4 py-2 sticky left-0 bg-rb-surface font-semibold text-slate-300">
                                  <div className="flex items-center gap-1.5">
                                    <div className="w-1.5 h-1.5 rounded-full" style={{ background: CATEGORY_COLORS[def.category] }} />
                                    {def.label}
                                  </div>
                                </td>
                                <td className="px-3 py-2 text-right font-mono font-bold text-slate-200">
                                  {fmtVal(value)} <span className="text-slate-600 font-normal">{def.unit}</span>
                                </td>
                                {JURISDICTIONS.map(j => {
                                  const limit = getLimitsForStandard(j.code)[def.key] ?? 0;
                                  if (limit === 0) return <td key={j.code} className="px-3 py-2 text-center text-slate-700">—</td>;
                                  const pass = value <= limit;
                                  const ratio = value / limit;
                                  const color = !pass ? (ratio > 2 ? '#ef4444' : '#f97316') : ratio > 0.8 ? '#f59e0b' : '#10b981';
                                  return (
                                    <td key={j.code} className="px-3 py-2 text-center">
                                      <div className="flex flex-col items-center gap-0.5">
                                        {pass
                                          ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
                                          : <XCircle className="w-3.5 h-3.5 text-red-400" />}
                                        <span className="font-mono text-[9px]" style={{ color }}>
                                          {fmtVal(limit)}
                                        </span>
                                      </div>
                                    </td>
                                  );
                                })}
                              </tr>
                            );
                          })}
                        </tbody>
                        <tfoot>
                          <tr className="border-t-2 border-slate-800/60 bg-rb-elevated">
                            <td className="px-4 py-2.5 font-bold text-slate-300 sticky left-0 bg-rb-elevated" colSpan={2}>Compliance Score</td>
                            {allJurisdictionScores.map(j => (
                              <td key={j.code} className="px-3 py-2.5 text-center">
                                <span className="text-sm font-bold" style={{ color: j.accentColor }}>{j.complianceScore}%</span>
                              </td>
                            ))}
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {/* ── PARAMETER DETAIL ────────────────────────────────────────── */}
              {view === 'detail' && (
                <div className="space-y-3">
                  {/* Category filter pills */}
                  <div className="flex gap-2 flex-wrap">
                    <button onClick={() => setFilterCategory(null)}
                      className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all ${!filterCategory ? 'bg-slate-700 text-slate-200' : 'bg-rb-elevated text-slate-500 hover:text-slate-300'}`}>
                      All ({complianceResults.length})
                    </button>
                    {['organic', 'physical', 'nutrients', 'metals', 'other'].map(cat => {
                      const catResults = complianceResults.filter(r => PARAM_DEFS.find(d => d.key === r.key)?.category === cat);
                      if (catResults.length === 0) return null;
                      return (
                        <button key={cat} onClick={() => setFilterCategory(filterCategory === cat ? null : cat)}
                          className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all capitalize ${filterCategory === cat ? 'text-slate-900' : 'bg-rb-elevated text-slate-500 hover:text-slate-300'}`}
                          style={filterCategory === cat ? { background: CATEGORY_COLORS[cat] } : { borderColor: `${CATEGORY_COLORS[cat]}40`, border: '1px solid' }}>
                          {cat} ({catResults.length})
                        </button>
                      );
                    })}
                  </div>

                  {/* Detailed table */}
                  <div className="rb-card overflow-hidden">
                    <div className="px-5 py-3.5 border-b border-slate-800/50 bg-rb-elevated/50 flex items-center justify-between">
                      <span className="text-sm font-bold text-slate-200">
                        {activeJ.flag} {activeJ.name} — Detailed Parameter Validation
                      </span>
                      <span className="text-[11px] text-slate-500">{filteredResults.length} parameters shown</span>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="text-[10px] uppercase tracking-wider text-slate-500 bg-rb-elevated">
                            <th className="text-left px-4 py-2.5 font-bold">Parameter</th>
                            <th className="text-left px-3 py-2.5 font-bold">Category</th>
                            <th className="text-right px-3 py-2.5 font-bold">Measured</th>
                            <th className="text-right px-3 py-2.5 font-bold">Limit</th>
                            <th className="text-right px-3 py-2.5 font-bold">Ratio</th>
                            <th className="px-3 py-2.5 font-bold" style={{ minWidth: 100 }}>Progress</th>
                            <th className="text-center px-3 py-2.5 font-bold">Margin</th>
                            <th className="text-center px-3 py-2.5 font-bold">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredResults.map(r => {
                            const cat = PARAM_DEFS.find(d => d.key === r.key)?.category ?? 'other';
                            return (
                              <tr key={r.key} className={`border-t border-slate-800/40 hover:bg-rb-elevated/50 ${!r.pass ? 'bg-red-500/3' : ''}`}>
                                <td className="px-4 py-2.5 font-semibold text-slate-200">{r.label}</td>
                                <td className="px-3 py-2.5">
                                  <span className="text-[10px] font-bold capitalize px-1.5 py-0.5 rounded" style={{ background: `${CATEGORY_COLORS[cat]}20`, color: CATEGORY_COLORS[cat] }}>{cat}</span>
                                </td>
                                <td className="px-3 py-2.5 text-right font-mono font-bold" style={{ color: r.color }}>
                                  {fmtVal(r.value)} <span className="text-slate-600 font-normal text-[10px]">{r.unit}</span>
                                </td>
                                <td className="px-3 py-2.5 text-right font-mono text-slate-400">
                                  {fmtVal(r.limit)} <span className="text-slate-600 text-[10px]">{r.unit}</span>
                                </td>
                                <td className="px-3 py-2.5 text-right font-mono font-bold" style={{ color: r.color }}>
                                  {(r.ratio * 100).toFixed(0)}%
                                </td>
                                <td className="px-3 py-2.5">
                                  <div className="w-full h-2 bg-slate-800 rounded-full overflow-hidden">
                                    <div className="h-full rounded-full transition-all" style={{ width: `${Math.min(100, r.ratio * 100)}%`, background: r.color }} />
                                  </div>
                                </td>
                                <td className="px-3 py-2.5 text-center font-mono text-[11px]" style={{ color: r.pass ? '#10b981' : '#f97316' }}>
                                  {r.pass ? `${Math.round(r.margin)}% below` : `${Math.round(Math.abs(r.margin))}% above`}
                                </td>
                                <td className="px-3 py-2.5 text-center">
                                  <StatusPill status={r.status} />
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Exceedance corrective actions */}
                  {complianceResults.filter(r => !r.pass).length > 0 && (
                    <div className="rb-card p-5">
                      <div className="text-sm font-bold text-slate-200 mb-4 flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4 text-red-400" />
                        Corrective Actions for {activeJ.shortLabel} Exceedances
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {complianceResults.filter(r => !r.pass).map(r => (
                          <div key={r.key} className="p-3.5 rounded-xl bg-rb-elevated border border-slate-800/40">
                            <div className="flex items-center gap-2 mb-2.5">
                              <XCircle className="w-4 h-4 text-red-400 shrink-0" />
                              <span className="text-sm font-bold text-slate-200">{r.label}</span>
                              <span className="text-xs font-mono text-red-400 ml-auto">{fmtVal(r.value)} / {fmtVal(r.limit)} {r.unit}</span>
                            </div>
                            {(CORRECTIVE_ACTIONS[r.key] ?? ['Review treatment performance', 'Increase chemical dosing if applicable', 'Inspect equipment', 'Contact process engineer']).map((a, i) => (
                              <div key={i} className="flex items-start gap-1.5 text-xs text-slate-400 py-0.5">
                                <ArrowRight className="w-3 h-3 text-blue-400 mt-0.5 shrink-0" />{a}
                              </div>
                            ))}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* ── REPORT ──────────────────────────────────────────────────── */}
              {view === 'report' && (
                <div className="space-y-4">
                  <div className="flex justify-end gap-2">
                    <button onClick={() => window.print()}
                      className="px-4 py-2 text-xs font-bold text-slate-300 bg-rb-elevated border border-slate-800/60 rounded-xl flex items-center gap-2">
                      <Printer className="w-3.5 h-3.5" /> Print A4 Report
                    </button>
                    <button onClick={handleSaveReport} disabled={saving}
                      className="px-4 py-2 text-xs font-bold text-black bg-amber-400 hover:bg-amber-300 rounded-xl flex items-center gap-2">
                      <Save className="w-3.5 h-3.5" />{saving ? 'Saving…' : 'Save to Database'}
                    </button>
                  </div>

                  <div className="rb-card p-6 space-y-5">
                    {/* Report header */}
                    <div className="border-b border-slate-800/50 pb-5 flex items-start justify-between gap-4">
                      <div>
                        <h2 className="text-xl font-bold text-slate-100">Regulatory Compliance Report</h2>
                        <p className="text-sm text-slate-500 mt-0.5">{selected.sampleName}</p>
                        {currentCustomer && <p className="text-sm text-slate-400">{currentCustomer.name} / {currentCustomer.company}</p>}
                      </div>
                      <div className="text-right text-xs text-slate-500 shrink-0">
                        <div>Generated: {new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })}</div>
                        <div>Validated: {lastValidated.toLocaleTimeString('en-GB')}</div>
                        <div className="mt-1 font-bold" style={{ color: activeJ.accentColor }}>{activeJ.flag} {activeJ.shortLabel}</div>
                        <div className="font-bold" style={{ color: scoreColor }}>{complianceScore}% Compliant</div>
                      </div>
                    </div>

                    {/* Executive summary */}
                    <div>
                      <h3 className="text-sm font-bold text-slate-200 mb-2">Executive Summary</h3>
                      <div className="p-4 rounded-xl bg-rb-elevated border border-slate-800/40 text-xs text-slate-400 leading-relaxed space-y-1.5">
                        <p>Sample <strong className="text-slate-200">{selected.sampleName}</strong> was validated against <strong className="text-slate-200">{activeJ.name}</strong> ({new Date(selected.createdAt).toLocaleDateString('en-GB')}).</p>
                        <p>Overall compliance: <strong style={{ color: scoreColor }}>{complianceScore}%</strong> — {complianceResults.filter(r => r.pass).length} of {complianceResults.length} parameters within consent limits.</p>
                        {complianceResults.filter(r => !r.pass).length > 0 && (
                          <p><strong className="text-red-400">Exceedances:</strong> {complianceResults.filter(r => !r.pass).map(r => r.label).join(', ')}.</p>
                        )}
                      </div>
                    </div>

                    {/* Jurisdiction comparison summary */}
                    <div>
                      <h3 className="text-sm font-bold text-slate-200 mb-3">Multi-Jurisdiction Summary</h3>
                      <div className="grid grid-cols-5 gap-2">
                        {allJurisdictionScores.map(j => {
                          const c = j.complianceScore >= 90 ? '#10b981' : j.complianceScore >= 70 ? '#f59e0b' : '#ef4444';
                          return (
                            <div key={j.code} className={`p-2.5 rounded-xl border text-center ${j.code === activeJurisdiction ? 'border-opacity-60' : 'border-slate-800/40'}`}
                              style={j.code === activeJurisdiction ? { borderColor: j.accentColor, background: `${j.accentColor}0A` } : {}}>
                              <div className="text-lg">{j.flag}</div>
                              <div className="text-[10px] font-bold text-slate-400">{j.shortLabel}</div>
                              <div className="text-base font-bold" style={{ color: c }}>{j.complianceScore}%</div>
                              <div className="text-[9px] text-slate-600">{j.failures.length} fail{j.failures.length !== 1 ? 's' : ''}</div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Full compliance table */}
                    <div>
                      <h3 className="text-sm font-bold text-slate-200 mb-2">{activeJ.shortLabel} — Detailed Results</h3>
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="bg-rb-elevated text-slate-500 text-[10px] uppercase tracking-wider">
                            <th className="text-left px-3 py-2 font-bold">Parameter</th>
                            <th className="text-right px-3 py-2 font-bold">Measured</th>
                            <th className="text-right px-3 py-2 font-bold">Consent Limit</th>
                            <th className="text-center px-3 py-2 font-bold">Result</th>
                          </tr>
                        </thead>
                        <tbody>
                          {complianceResults.map(r => (
                            <tr key={r.key} className="border-t border-slate-800/40">
                              <td className="px-3 py-2 text-slate-400">{r.label}</td>
                              <td className="px-3 py-2 text-right font-mono font-bold" style={{ color: r.color }}>{fmtVal(r.value)} {r.unit}</td>
                              <td className="px-3 py-2 text-right font-mono text-slate-500">{fmtVal(r.limit)} {r.unit}</td>
                              <td className="px-3 py-2 text-center">
                                {r.pass
                                  ? <span className="text-[10px] font-bold text-emerald-400">PASS</span>
                                  : <span className="text-[10px] font-bold text-red-400">FAIL</span>}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    <div className="pt-3 border-t border-slate-800/40 flex justify-between text-xs text-slate-600">
                      <span>Reynolds & Bauhm — Water Treatment Engineering</span>
                      <span>This report is generated automatically and is for guidance only. Confirm with regulatory authority.</span>
                    </div>
                  </div>
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        </>
      )}
    </div>
  );
}

// Corrective actions shared reference (same as EffluentStudyPage)
const CORRECTIVE_ACTIONS: Record<string, string[]> = {
  bod5: ['Increase aeration intensity or SRT', 'Check MLSS concentration and F/M ratio', 'Review influent loading — possible organic shock', 'Consider PAC addition or GAC polishing'],
  cod:  ['Review refractory COD fraction (BOD:COD ratio)', 'Optimise coagulation/flocculation', 'Add GAC or advanced oxidation (Fenton/ozone)', 'Increase biological SRT or add MBBR carriers'],
  tss:  ['Inspect secondary clarifier for sludge blanket rise', 'Check RAS rate and MLSS control', 'Optimise polymer dosing for final filtration', 'Service/clean multimedia or cloth filter'],
  ph:   ['Review acid/alkali dosing rate and setpoint', 'Check dosing pump calibration', 'Inspect pH probe for fouling or drift', 'Review buffering capacity of treated effluent'],
  ammoniaN: ['Confirm nitrification is occurring (DO ≥2 mg/L)', 'Check SRT — nitrifiers need SRT >8 d at 15°C', 'Supplement alkalinity if buffer depleted', 'Review aeration rate in nitrification zone'],
  totalNitrogen: ['Implement anoxic zone for denitrification', 'Optimise internal recycle ratio (3:1 to 5:1)', 'Check carbon source availability for denitrification', 'Consider methanol or glycerol addition'],
  totalPhosphorus: ['Increase FeCl₃ or alum dosing dose', 'Check chemical dosing point location', 'Implement Enhanced Biological P Removal (EBPR)', 'Consider polishing with coagulation/filtration'],
  oilGrease: ['Increase DAF recycle rate or pressure', 'Review coagulant/polymer dosing to DAF', 'Check flotation blanket depth and skimmer operation', 'Inspect grease trap before DAF'],
  tds:  ['Reduce TDS at source (process water reuse)', 'Consider reverse osmosis or nanofiltration', 'Review saline water ingress to system', 'Assess industrial discharge controls'],
  lead: ['Increase pH to >8.5 for metal precipitation', 'Dose sulphide for lead sulphide precipitation', 'Add coagulant and polymer for floc removal', 'Check upstream source and segregate industrial discharge'],
  chromium: ['Reduce Cr(VI) to Cr(III) with sulphite/ferrous', 'Precipitate at pH 8–9 with lime', 'Consider ion exchange for low concentrations', 'Source control and trade effluent consent review'],
};
